import { ExternalLink, Plus } from 'lucide-react';
import { Badge, ProviderLogo } from '@/components/ui';
import type { McpProviderCatalogEntry } from '../../../types/mcp';

interface McpProviderPickerProps {
  providers: McpProviderCatalogEntry[];
  customServerAllowed: boolean;
  onSelectProvider: (providerId: string) => void;
  onSelectCustom: () => void;
}

function statusBadge(status: McpProviderCatalogEntry['status']) {
  if (status === 'beta') return <Badge variant="warning">Beta</Badge>;
  if (status === 'experimental')
    return <Badge variant="purple">Experimental</Badge>;
  return null;
}

export function McpProviderPicker({
  providers,
  customServerAllowed,
  onSelectProvider,
  onSelectCustom,
}: McpProviderPickerProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {providers.map((provider) => {
        const unavailable = provider.authMethods.length === 0;
        return (
          <button
            key={provider.providerId}
            type="button"
            onClick={() => {
              if (!unavailable) onSelectProvider(provider.providerId);
            }}
            disabled={unavailable}
            aria-disabled={unavailable}
            className={`text-left rounded-lg border border-border bg-card p-5 flex flex-col gap-3 transition-colors ${
              unavailable
                ? 'opacity-60 cursor-not-allowed'
                : 'hover:border-primary-500 hover:bg-accent/30 cursor-pointer'
            }`}
          >
            <div className="flex items-start gap-3">
              <ProviderLogo
                slug={provider.providerId}
                displayName={provider.displayName}
                grayscale
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-foreground">
                    {provider.displayName}
                  </h3>
                  {statusBadge(provider.status)}
                  {unavailable && <Badge variant="gray">Coming soon</Badge>}
                </div>
                {provider.category && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {provider.category}
                  </p>
                )}
              </div>
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2">
              {provider.description}
            </p>
            {provider.documentationUrl && (
              <a
                href={provider.documentationUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-500"
              >
                Documentation <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </button>
        );
      })}
      {customServerAllowed && (
        <button
          type="button"
          onClick={onSelectCustom}
          className="text-left rounded-lg border border-dashed border-border bg-card p-5 flex flex-col gap-3 hover:border-primary-500 hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center">
              <Plus className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-foreground">
                Custom MCP server
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Bring your own
              </p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground line-clamp-2">
            Connect any MCP-compatible endpoint by URL.
          </p>
        </button>
      )}
    </div>
  );
}
